<template>
  <div
    ref="gameContainer"
    class="maze-game"
    tabindex="0"
    @keydown="handleKeydown"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd"
  >
    <canvas ref="canvas" :width="width * cellSize" :height="height * cellSize" class="maze-canvas" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, type Ref, ref, watch } from 'vue'

interface Cell {
  x: number
  y: number
}

interface Direction {
  x: number
  y: number
  name: string
}

interface MazeGeneratedEvent {
  width: number
  height: number
  cellSize: number
}

interface GameCompletedEvent {
  moves: number
  time: number
}

// Props
interface Props {
  width?: number
  height?: number
  cellSize?: number
}

const props = withDefaults(defineProps<Props>(), {
  width: 25,
  height: 20,
  cellSize: 15,
})

// Emits
const emit = defineEmits<{
  'maze-generated': [event: MazeGeneratedEvent]
  'game-completed': [event: GameCompletedEvent]
  'game-started': []
  'game-reset': []
}>()

// Refs
const canvas: Ref<HTMLCanvasElement | null> = ref(null)
const gameContainer: Ref<HTMLDivElement | null> = ref(null)
const ctx: Ref<CanvasRenderingContext2D | null> = ref(null)
const status = ref('Generating maze...')

// Maze state
const grid: Ref<number[][]> = ref([])
const hWalls: Ref<boolean[][]> = ref([])
const vWalls: Ref<boolean[][]> = ref([])
const isGenerating = ref(true)

// Game state
const player: Ref<Cell> = ref({ x: 0, y: 0 })
const isPlaying = ref(false)
const gameWon = ref(false)

// Touch state
const touchStartX = ref(0)
const touchStartY = ref(0)
const minSwipeDistance = 30 // Minimum pixels for a swipe to register

// Directions: North, East, South, West
const directions: Direction[] = [
  { x: 0, y: -1, name: 'N' },
  { x: 1, y: 0, name: 'E' },
  { x: 0, y: 1, name: 'S' },
  { x: -1, y: 0, name: 'W' },
]

const resetMaze = (): void => {
  // Grid represents cells: 0 = unvisited, 1 = in maze
  grid.value = Array(props.height)
    .fill(null)
    .map(() => Array(props.width).fill(0))

  // Walls between cells (horizontal and vertical)
  hWalls.value = Array(props.height + 1)
    .fill(null)
    .map(() => Array(props.width).fill(true))
  vWalls.value = Array(props.height)
    .fill(null)
    .map(() => Array(props.width + 1).fill(true))

  // Reset game state
  player.value = { x: 0, y: 0 }
  isPlaying.value = false
  gameWon.value = false
}

const draw = (): void => {
  if (!ctx.value) return

  ctx.value.clearRect(0, 0, canvas.value!.width, canvas.value!.height)

  // Draw cells
  for (let y = 0; y < props.height; y++) {
    for (let x = 0; x < props.width; x++) {
      const cellX = x * props.cellSize
      const cellY = y * props.cellSize

      // Color cells - all maze cells are white
      ctx.value.fillStyle = '#fff'
      ctx.value.fillRect(cellX, cellY, props.cellSize, props.cellSize)
    }
  }

  // Draw walls
  ctx.value.strokeStyle = '#333'
  ctx.value.lineWidth = 2
  ctx.value.beginPath()

  // Horizontal walls
  for (let y = 0; y <= props.height; y++) {
    for (let x = 0; x < props.width; x++) {
      if (hWalls.value[y][x]) {
        ctx.value.moveTo(x * props.cellSize, y * props.cellSize)
        ctx.value.lineTo((x + 1) * props.cellSize, y * props.cellSize)
      }
    }
  }

  // Vertical walls
  for (let y = 0; y < props.height; y++) {
    for (let x = 0; x <= props.width; x++) {
      if (vWalls.value[y][x]) {
        ctx.value.moveTo(x * props.cellSize, y * props.cellSize)
        ctx.value.lineTo(x * props.cellSize, (y + 1) * props.cellSize)
      }
    }
  }

  ctx.value.stroke()

  // Draw player
  if (isPlaying.value) {
    ctx.value.fillStyle = '#008f51'
    const playerCenterX = player.value.x * props.cellSize + props.cellSize / 2
    const playerCenterY = player.value.y * props.cellSize + props.cellSize / 2
    const radius = Math.min(props.cellSize / 3, 8)

    ctx.value.beginPath()
    ctx.value.arc(playerCenterX, playerCenterY, radius, 0, 2 * Math.PI)
    ctx.value.fill()
  }

  // Draw start and end markers (except when game is won)
  if (!gameWon.value) {
    ctx.value.fillStyle = '#f59e0b'
    const endX = (props.width - 1) * props.cellSize + props.cellSize / 2
    const endY = (props.height - 1) * props.cellSize + props.cellSize / 2
    const radius = Math.min(props.cellSize / 3, 8)

    ctx.value.beginPath()
    ctx.value.arc(endX, endY, radius, 0, 2 * Math.PI)
    ctx.value.fill()
  }
}

const isValidCell = (x: number, y: number): boolean => {
  return x >= 0 && x < props.width && y >= 0 && y < props.height
}

const removeWall = (x1: number, y1: number, x2: number, y2: number): void => {
  if (x1 === x2) {
    // Horizontal wall
    const wallY = Math.max(y1, y2)
    hWalls.value[wallY][x1] = false
  } else {
    // Vertical wall
    const wallX = Math.max(x1, x2)
    vWalls.value[y1][wallX] = false
  }
}

const getRandomDirection = (): Direction => {
  return directions[Math.floor(Math.random() * directions.length)]
}

const performRandomWalk = (startX: number, startY: number): Cell[] => {
  const walk: Cell[] = [{ x: startX, y: startY }]
  const walkSet = new Set([`${startX},${startY}`])
  let current = { x: startX, y: startY }

  while (true) {
    const direction = getRandomDirection()
    const next = {
      x: current.x + direction.x,
      y: current.y + direction.y,
    }

    if (!isValidCell(next.x, next.y)) {
      continue
    }

    // If we hit a cell already in the maze, we're done
    if (grid.value[next.y][next.x] === 1) {
      walk.push(next)
      return walk
    }

    const nextKey = `${next.x},${next.y}`

    // If we hit our own walk, erase the loop
    if (walkSet.has(nextKey)) {
      const loopStart = walk.findIndex((cell) => cell.x === next.x && cell.y === next.y)
      // Remove the loop
      const removed = walk.splice(loopStart + 1)
      removed.forEach((cell) => walkSet.delete(`${cell.x},${cell.y}`))
      current = next
    } else {
      // Continue the walk
      walk.push(next)
      walkSet.add(nextKey)
      current = next
    }
  }
}

const addWalkToMaze = (walk: Cell[]): void => {
  // Add all cells in the walk to the maze
  for (const cell of walk) {
    grid.value[cell.y][cell.x] = 1
  }

  // Remove walls between consecutive cells in the walk
  for (let i = 0; i < walk.length - 1; i++) {
    removeWall(walk[i].x, walk[i].y, walk[i + 1].x, walk[i + 1].y)
  }
}

const generateMaze = (): void => {
  resetMaze()
  isGenerating.value = true
  status.value = 'Generating maze...'

  // Start with a random cell
  const startX = Math.floor(Math.random() * props.width)
  const startY = Math.floor(Math.random() * props.height)
  grid.value[startY][startX] = 1

  let cellsInMaze = 1
  const totalCells = props.width * props.height

  while (cellsInMaze < totalCells) {
    // Find a random unvisited cell
    const unvisited: Cell[] = []

    for (let y = 0; y < props.height; y++) {
      for (let x = 0; x < props.width; x++) {
        if (grid.value[y][x] === 0) {
          unvisited.push({ x, y })
        }
      }
    }

    if (unvisited.length === 0) break

    const startCell = unvisited[Math.floor(Math.random() * unvisited.length)]

    // Perform random walk
    const walk = performRandomWalk(startCell.x, startCell.y)

    // Add walk to maze
    addWalkToMaze(walk)
    cellsInMaze += walk.length - 1 // -1 because last cell was already in maze
  }

  // Start the game immediately after generation
  isGenerating.value = false
  isPlaying.value = true
  player.value = { x: 0, y: 0 }
  status.value = 'Use WASD or Arrow Keys to navigate to the red exit!'

  draw()

  // Focus the game container for keyboard events
  if (gameContainer.value) {
    gameContainer.value.focus()
  }

  emit('maze-generated', {
    width: props.width,
    height: props.height,
    cellSize: props.cellSize,
  })
  emit('game-started')
}

const canMoveTo = (x: number, y: number): boolean => {
  if (!isValidCell(x, y)) return false

  const fromX = player.value.x
  const fromY = player.value.y

  // Check if there's a wall between current position and target
  if (x === fromX) {
    // Vertical movement
    const wallY = Math.max(fromY, y)
    return !hWalls.value[wallY][fromX]
  } else if (y === fromY) {
    // Horizontal movement
    const wallX = Math.max(fromX, x)
    return !vWalls.value[fromY][wallX]
  }

  return false
}

const movePlayer = (dx: number, dy: number) => {
  if (!isPlaying.value || gameWon.value) return

  const newX = player.value.x + dx
  const newY = player.value.y + dy

  if (canMoveTo(newX, newY)) {
    player.value.x = newX
    player.value.y = newY

    // Check for victory
    if (player.value.x === props.width - 1 && player.value.y === props.height - 1) {
      gameWon.value = true
      status.value = 'Congratulations! You solved the maze!'

      emit('game-completed', {
        moves: 0, // Could track actual moves if needed
        time: Date.now(), // Could track actual time if needed
      })
    }

    draw()
  }
}

const handleKeydown = (event: KeyboardEvent): void => {
  if (!isPlaying.value || gameWon.value) return

  switch (event.key.toLowerCase()) {
    case 'w':
    case 'arrowup':
      movePlayer(0, -1)
      event.preventDefault()
      break
    case 's':
    case 'arrowdown':
      movePlayer(0, 1)
      event.preventDefault()
      break
    case 'a':
    case 'arrowleft':
      movePlayer(-1, 0)
      event.preventDefault()
      break
    case 'd':
    case 'arrowright':
      movePlayer(1, 0)
      event.preventDefault()
      break
  }
}

const handleTouchStart = (event: TouchEvent): void => {
  if (!isPlaying.value || gameWon.value) return

  const touch = event.touches[0]
  touchStartX.value = touch.clientX
  touchStartY.value = touch.clientY
}

const handleTouchEnd = (event: TouchEvent): void => {
  if (!isPlaying.value || gameWon.value) return

  const touch = event.changedTouches[0]
  const deltaX = touch.clientX - touchStartX.value
  const deltaY = touch.clientY - touchStartY.value

  const absDeltaX = Math.abs(deltaX)
  const absDeltaY = Math.abs(deltaY)

  // Check if swipe distance is sufficient
  if (absDeltaX < minSwipeDistance && absDeltaY < minSwipeDistance) {
    return
  }

  // Determine primary swipe direction
  if (absDeltaX > absDeltaY) {
    // Horizontal swipe
    if (deltaX > 0) {
      movePlayer(1, 0) // Right
    } else {
      movePlayer(-1, 0) // Left
    }
  } else {
    // Vertical swipe
    if (deltaY > 0) {
      movePlayer(0, 1) // Down
    } else {
      movePlayer(0, -1) // Up
    }
  }

  event.preventDefault()
}

// Watch for prop changes and regenerate canvas
watch([() => props.width, () => props.height, () => props.cellSize], () => {
  if (canvas.value) {
    canvas.value.width = props.width * props.cellSize
    canvas.value.height = props.height * props.cellSize
    if (isPlaying.value) {
      draw()
    }
  }
})

onMounted(() => {
  if (canvas.value) {
    ctx.value = canvas.value.getContext('2d')
  }

  // Generate maze and start game immediately on mount
  generateMaze()
})
</script>

<style scoped>
.maze-game {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  gap: 20px;
  outline: none; /* Remove focus outline */
}

.maze-game:focus {
  outline: none; /* Ensure no outline on focus */
}

.maze-canvas {
  border: 2px solid #333;
  border-radius: 3px;
  background: white;
  touch-action: none;
  user-select: none;
  user-select: none;
}

.status {
  max-width: 600px;
  color: #333;
  font-weight: 500;
  text-align: center;
}

.victory-message {
  color: #28a745;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
}
</style>
